import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, of, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, filter } from 'rxjs/operators';
import { ReusableStreamProperty } from '@ipreo/muni-shared';
import { GameDayRootReducerState } from '../../state/root.state';
import { toMillion } from '../../gameday/components/excel-export/excel.formatter';
import { ExportBaseService } from '../../gameday/services/export-base.service';
import { TabSettings } from '../../gameday/models/report';
import { ColumnDefinition } from '../../models/column';
import { ManagerOverviewReport } from '../models/manager';
import { ManagersOverviewService } from './managers-overview.service';
import { ManagersRetailService } from './managers-retail.service';
import { AccountsReportsService } from './accounts.service';
import { getGamedayDealState } from '../../state/data/reducer';
import { GameDayModel } from '../../models/gameday-model';

const NOCATEGORY_COLUMN: ColumnDefinition = {
  label: 'Retail',
  field: 'nocategory',
  position: 100,
  sortBy: undefined
};
const OTHER_COLUMN: ColumnDefinition = {
  label: 'Retail - Other',
  field: 'other',
  position: 101,
  sortBy: undefined
};
const LOW_PRIORITY_COLUMN: ColumnDefinition = {
  label: 'Retail - Other',
  field: 'lowPriority',
  position: 102,
  sortBy: undefined
};
const OVERVIEW_HEADERS: ColumnDefinition[] = [
  { label: 'Manager', field: 'name', position: 1, sortBy: undefined },
  { label: 'Retail', field: 'retailAmount', position: 2, sortBy: undefined },
  {
    label: 'Institutional',
    field: 'institutionalAmount',
    position: 3,
    sortBy: undefined
  },
  { label: 'Stock', field: 'stockAmount', position: 4, sortBy: undefined },
  { label: 'Total', field: 'total', position: 103, sortBy: undefined },
  { label: 'Manager Type', field: 'role', position: 104, sortBy: undefined }
];
const RETAIL_HEADERS: ColumnDefinition[] = [
  { label: 'Manager', field: 'name', position: 1, sortBy: undefined },
  { label: 'Retail Total', field: 'total', position: 103, sortBy: undefined },
  { label: 'Manager Type', field: 'role', position: 104, sortBy: undefined }
];
const ACCOUNTS_HEADERS: ColumnDefinition[] = [
  { label: 'Account', field: 'name', position: 1, sortBy: undefined },
  { label: 'Retail', field: 'retailAmount', position: 2, sortBy: undefined },
  {
    label: 'Institutional',
    field: 'institutionalAmount',
    position: 3,
    sortBy: undefined
  },
  { label: 'Total', field: 'total', position: 4, sortBy: undefined },
  { label: 'Money Type', field: 'moneyType', position: 5, sortBy: undefined }
];
const DYNAMIC_COLUMN_START_POSITION = 10;
const TOTAL_LABEL = 'Total';
const MANAGERS_OVERVIEW_LABEL = 'Managers Overview';
const MANAGERS_RETAIL_LABEL = 'Managers Retail';
const ACCOUNTS_LABEL = 'Accounts';
const MANAGERS_OVERVIEW_TAB = {
  name: MANAGERS_OVERVIEW_LABEL,
  priority: 5
};
const MANAGERS_RETAIL_TAB = {
  name: MANAGERS_RETAIL_LABEL,
  priority: 6
};
const ACCOUNTS_TAB = {
  name: ACCOUNTS_LABEL,
  priority: 7
};

@Injectable()
export class ExportReportsService extends ExportBaseService {
  public readonly hasAccountsPermissions$ = this.store.pipe(
    filter(state => !!state.gameDay.data.deal),
    select(getGamedayDealState),
    map(
      ({ gamedaySettings }: GameDayModel.Deal) =>
        gamedaySettings && gamedaySettings.showAccountNames
    ),
    distinctUntilChanged()
  );

  // Totals
  @ReusableStreamProperty
  private readonly overviewTotals$: Observable<
    ManagerOverviewReport
  > = this.managersOverviewService.getManagersOverviewTotalsByTypes();

  @ReusableStreamProperty
  private readonly retailTotals$ = this.managersRetailService
    .getManagersRetailTotalList;

  @ReusableStreamProperty
  private readonly accountsTotals$ = this.accountsReportsService.getAccountsTotalsByTypes();

  // Category column rules
  @ReusableStreamProperty
  private readonly isColumnsPresent$ = this.managersRetailService
    .isColumnsPresent;

  @ReusableStreamProperty
  private readonly isNoCategoryColumnPresent$ = combineLatest(
    this.managersRetailService.isNoCategoryColumnPresent,
    this.isColumnsPresent$
  ).pipe(map(([isNoCategory, isColumns]) => isNoCategory && !isColumns));

  @ReusableStreamProperty
  private readonly isOtherColumnPresent$ = combineLatest(
    this.managersRetailService.isNoCategoryColumnPresent,
    this.isColumnsPresent$
  ).pipe(map(([isNoCategory, isColumns]) => isNoCategory && isColumns));

  @ReusableStreamProperty
  private readonly isLowPriorityColumnPresent$ = combineLatest(
    this.retailTotals$,
    this.isColumnsPresent$
  ).pipe(
    map(([totals, isColumns]) => totals.lowPriorityTotal !== 0 && !isColumns)
  );

  // Header data streams
  @ReusableStreamProperty
  private readonly retailHeaders$ = combineLatest(
    this.managersRetailService.getColumns,
    this.isNoCategoryColumnPresent$,
    this.isOtherColumnPresent$,
    this.isLowPriorityColumnPresent$
  ).pipe(
    map(([columns, noCategory, other, lowPriority]) =>
      this.combineMonitorRetailHeaders(columns, noCategory, other, lowPriority)
    )
  );

  // Table data streams
  @ReusableStreamProperty
  private readonly overviewData$: Observable<
    ManagerOverviewReport[]
  > = this.managersOverviewService.getManagersOverviewData();

  @ReusableStreamProperty
  private readonly retailData$ = this.managersRetailService
    .getManagersRetailList;

  @ReusableStreamProperty
  private readonly accountsData$ = this.accountsReportsService.getAccountAmountsData();

  // Managers and accounts stream that combines table data and headers
  // tslint:disable-next-line
  public readonly reportsData$: Observable<TabSettings[]> = combineLatest(
    of(OVERVIEW_HEADERS),
    this.overviewTotals$,
    this.overviewData$,
    this.retailHeaders$,
    this.retailTotals$,
    this.retailData$,
    of(ACCOUNTS_HEADERS),
    this.accountsTotals$,
    this.accountsData$,
    this.hasAccountsPermissions$
  ).pipe(map(([...params]) => this.getTabs(params)));

  constructor(
    private managersOverviewService: ManagersOverviewService,
    private managersRetailService: ManagersRetailService,
    private accountsReportsService: AccountsReportsService,
    private store: Store<GameDayRootReducerState>
  ) {
    super();
  }

  private combineMonitorRetailHeaders(
    columns,
    nocategory,
    other,
    lowPriority
  ): ColumnDefinition[] {
    const retailColumns: ColumnDefinition[] = [...RETAIL_HEADERS];
    const formattedDynamicColumns: ColumnDefinition[] = columns.map(
      (column, index) => ({
        label: column,
        field: column,
        position: index + DYNAMIC_COLUMN_START_POSITION,
        sortBy: undefined
      })
    );

    if (nocategory) {
      retailColumns.push(NOCATEGORY_COLUMN);
    }

    if (other) {
      retailColumns.push(OTHER_COLUMN);
    }

    if (lowPriority) {
      retailColumns.push(LOW_PRIORITY_COLUMN);
    }

    return retailColumns
      .concat(formattedDynamicColumns)
      .sort((a, b) => a.position - b.position);
  }

  private getTabs(data): TabSettings[] {
    const [
      overviewHeaders,
      overviewTotals,
      overviewData,
      retailHeaders,
      retailTotals,
      retailData,
      accountsHeaders,
      accountsTotals,
      accountsData,
      hasAccountsPermissions
    ] = data;

    const tabs: TabSettings[] = [
      this.createTab({
        ...MANAGERS_OVERVIEW_TAB,
        header: overviewHeaders,
        totals: toMillion({ ...overviewTotals, name: TOTAL_LABEL }),
        data: toMillion(overviewData)
      }),
      this.createTab({
        ...MANAGERS_RETAIL_TAB,
        header: retailHeaders,
        totals: toMillion({
          ...retailTotals,
          name: TOTAL_LABEL,
          other: retailTotals.otherTotal,
          lowPriority: retailTotals.lowPriorityTotal,
          nocategory: retailTotals.nocategoryTotal
        }),
        data: toMillion(retailData)
      })
    ];

    if (hasAccountsPermissions) {
      tabs.push(
        this.createTab({
          ...ACCOUNTS_TAB,
          header: accountsHeaders,
          totals: toMillion({
            ...accountsTotals,
            name: `${accountsData.length} ${ACCOUNTS_LABEL}`
          }),
          data: toMillion(accountsData)
        })
      );
    }

    return tabs;
  }
}
